import React, { useEffect, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import FormField from "components/FormField";
import { useTranslation } from "react-i18next";

const normalizeHex = (hex) => {
  if (!hex) return null;
  const clean = String(hex).trim().replace(/^#/, "");
  if (/^[0-9a-fA-F]{3}$/.test(clean)) {
    return `#${clean
      .split("")
      .map((ch) => `${ch}${ch}`)
      .join("")
      .toUpperCase()}`;
  }
  if (/^[0-9a-fA-F]{6}$/.test(clean)) {
    return `#${clean.toUpperCase()}`;
  }
  return null;
};

function ColorPickerField({ input, label, placeholder = "#28363f" }) {
  const { t } = useTranslation();
  const [hexDraft, setHexDraft] = useState(input.value ?? "");
  const [applyError, setApplyError] = useState("");

  useEffect(() => {
    setHexDraft(input.value ?? "");
  }, [input.value]);

  const appliedHex = useMemo(() => normalizeHex(input.value), [input.value]);
  const previewHex = useMemo(() => appliedHex, [appliedHex]);

  const onHexManualChange = useCallback((ev) => {
    const value = ev?.target?.value ?? "";
    setHexDraft(value);
    setApplyError("");
  }, []);

  const onApplyHex = useCallback(() => {
    const normalized = normalizeHex(hexDraft);
    if (!normalized) {
      setApplyError("notValidColor");
      return;
    }

    setApplyError("");
    if (appliedHex !== normalized) {
      input.onChange(normalized);
    }
    if (typeof input.onBlur === "function") {
      input.onBlur(normalized);
    }
    setHexDraft(normalized);
  }, [hexDraft, input, t, appliedHex]);

  const canApplyHex = useMemo(() => {
    const draft = String(hexDraft ?? "").trim();
    return draft.length > 0;
  }, [hexDraft]);

  const showHexError = Boolean(applyError);
  const hexHelperText = applyError || " ";

  return (
    <MDBox>
      {label ? (
        <MDTypography variant="button" fontWeight="regular" color="text" mb={1}>
          {label}
        </MDTypography>
      ) : null}

      <MDBox display="flex" alignItems="center" gap={2} mb={2}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            backgroundColor: previewHex || "transparent",
            border: "1px solid rgba(0,0,0,0.15)",
          }}
          title={previewHex || ""}
        />
        <MDTypography variant="body2" color="text">
          {previewHex || ""}
        </MDTypography>
      </MDBox>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <MDBox
            display="flex"
            alignItems="flex-end"
            gap={1}
            sx={{ width: "100%", maxWidth: { xs: "100%", sm: 340 } }}
          >
            <MDBox flex={1}>
              <FormField
                label="HEX"
                placeholder={placeholder}
                value={hexDraft}
                onChange={onHexManualChange}
                error={showHexError}
                helperText={hexHelperText}
              />
            </MDBox>
            <MDButton
              type="button"
              variant="outlined"
              color="dark"
              size="small"
              onClick={onApplyHex}
              disabled={!canApplyHex}
            >
              {t("applyColor")}
            </MDButton>
          </MDBox>
        </Grid>
      </Grid>
    </MDBox>
  );
}

ColorPickerField.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.any,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
  }).isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
};

ColorPickerField.defaultProps = {
  label: "",
  placeholder: "#28363f",
};

export default ColorPickerField;
